import { Plus } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useId, useState } from "react";

import type { Patient } from "@elation/api-client/src/stitch-web/models";

import { PRODUCT_NAME } from "@src/shared/brand";
import { Button } from "@src/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/shared/components/ui/popover";

import type { CreatePatientFormValues } from "../../../../domain";
import {
  CreatePatientFormV2,
  useCreatePatientFormSubmission,
} from "../CreatePatientForm";

export interface CreatePatientPopoverV2Props {
  align?: "center" | "end" | "start";
  canCreatePatient?: boolean;
  defaultOpen?: boolean;
  initialValues?: Partial<CreatePatientFormValues>;
  onCreated?: (patient: Patient | null) => Promise<void> | void;
  side?: "bottom" | "left" | "right" | "top";
  trigger?: ReactElement;
  triggerLabel?: ReactNode;
}

export function CreatePatientPopoverV2({
  align = "start",
  canCreatePatient = true,
  defaultOpen = false,
  initialValues,
  onCreated,
  side = "bottom",
  trigger,
  triggerLabel = "Create new patient",
}: CreatePatientPopoverV2Props) {
  const [open, setOpen] = useState(defaultOpen);
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const descriptionId = `${generatedId}-description`;
  const { error, isSubmitting, reset, submitFormValues } =
    useCreatePatientFormSubmission({ onCreated });

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting && !nextOpen) return;
    setOpen(nextOpen);
    if (!nextOpen) reset();
  };

  const handleSubmit = async (values: CreatePatientFormValues) => {
    await submitFormValues(values);
    handleOpenChange(false);
  };

  if (!canCreatePatient) return null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="ghost"
            className="h-auto gap-1.5 rounded-[7px] px-0 py-0 text-[12.5px] font-semibold text-primary hover:bg-transparent hover:text-primary"
          >
            <Plus className="size-3.5" aria-hidden="true" />
            {triggerLabel}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={8}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="w-[min(calc(100vw-2rem),560px)] rounded-[18px] border-border p-5 shadow-soft"
      >
        <div className="mb-5 space-y-1">
          <h2
            id={titleId}
            className="text-[20px] font-semibold leading-none tracking-[-0.02em] text-surface-foreground"
          >
            New patient
          </h2>
          <p id={descriptionId} className="sr-only">
            Enter the patient&apos;s demographics to create their{" "}
            {PRODUCT_NAME.singular} record.
          </p>
        </div>

        <CreatePatientFormV2
          error={error}
          initialValues={initialValues}
          isSubmitting={isSubmitting}
          onCancel={() => handleOpenChange(false)}
          onSubmit={handleSubmit}
        />
      </PopoverContent>
    </Popover>
  );
}
